import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { StatePanel } from '@3dspinit/ui';
import { api } from '../lib/api';

type Connector = {
  id: string;
  name: string;
  category: string;
  status: string;
  operation: string;
  prerequisites: string;
  lastSuccessfulTest: string | null;
  supportRoute: string;
  formats: string[];
};

type Tab = 'tools' | 'worlds';

const STATUS_LABEL: Record<string, string> = {
  available: 'Available',
  beta: 'Beta',
  'manual-export': 'Manual export',
  planned: 'Planned',
  'not-configured': 'Not configured',
  blocked: 'Blocked',
};

export function ConnectPage() {
  const [tab, setTab] = useState<Tab>('tools');
  const [connectors, setConnectors] = useState<Connector[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api
      .getConnectors()
      .then((r) => setConnectors(r.connectors as Connector[]))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load connectors'));
  }, []);

  const filtered = connectors.filter((c) =>
    tab === 'tools' ? c.category !== 'destination' : c.category === 'destination',
  );

  return (
    <main className="page">
      <h1 style={{ fontFamily: 'var(--font-display)', letterSpacing: '-0.03em' }}>Connect</h1>
      <p className="muted" style={{ maxWidth: '52ch' }}>
        Authorized imports and destination handoffs. Status values are truthful — a logo is not a working integration.
      </p>

      <div role="tablist" aria-label="Connect sections" className="row" style={{ margin: '1.5rem 0 1rem' }}>
        <button
          type="button"
          className={`btn ${tab === 'tools' ? 'primary' : ''}`}
          role="tab"
          aria-selected={tab === 'tools'}
          onClick={() => setTab('tools')}
        >
          Tools &amp; Libraries
        </button>
        <button
          type="button"
          className={`btn ${tab === 'worlds' ? 'primary' : ''}`}
          role="tab"
          aria-selected={tab === 'worlds'}
          onClick={() => setTab('worlds')}
        >
          Worlds &amp; Platforms
        </button>
      </div>

      {error && <p className="err">{error}</p>}

      <div className="card-grid">
        {filtered.map((c) => (
          <article key={c.id} className="panel">
            <div className="row" style={{ justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0, fontSize: '1.0625rem' }}>{c.name}</h2>
              <span className="muted" style={{ fontSize: '0.8125rem', fontWeight: 600 }}>
                {STATUS_LABEL[c.status] ?? c.status}
              </span>
            </div>
            <p style={{ margin: '0.75rem 0 0.35rem', fontSize: '0.875rem' }}>{c.operation}</p>
            <p className="muted" style={{ margin: 0, fontSize: '0.8125rem' }}>
              {c.prerequisites}
            </p>
            <p className="muted" style={{ margin: '0.5rem 0 0', fontSize: '0.75rem' }}>
              Formats: {c.formats.join(', ') || '—'} · Last test:{' '}
              {c.lastSuccessfulTest ?? 'never'}
            </p>
            <div className="row" style={{ marginTop: '0.75rem' }}>
              <Link className="btn" to={c.supportRoute}>
                {c.status === 'manual-export' || c.status === 'beta' ? 'Upload / continue' : 'Details'}
              </Link>
            </div>
          </article>
        ))}
      </div>

      {!filtered.length && !error && <p className="muted">No connectors in this tab yet.</p>}

      <StatePanel
        variant="default"
        title="First authoring path"
        message="Until a connector is Available, use Upload for GLB/glTF. Destination publication requires a verified receipt — never from a button click alone."
        actionLabel="Open Create workspace"
        onAction={() => {
          window.location.href = '/workspace';
        }}
      />
    </main>
  );
}
